import React, {
  ComponentProps,
  FC,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { styled } from '@linaria/react'

import { VoidHandler } from 'common/types'
import { GQLPublicInterest } from 'generated-graphql/graphql'

import { matchColor, Tag } from './Tag'
import { TagTypes } from './Tag.types'
import { More } from '../Typography/More/More'

const tagHeight: number = 36

export const Tags: FC<{
  tags: GQLPublicInterest[]
  rowsCount?: number
  onClick: VoidHandler
  onMoreClick?: ComponentProps<typeof More>['onClick']
  onTagDelete?: ComponentProps<typeof Tag>['onTagDelete']
}> = ({ tags, rowsCount = 3, onClick, onMoreClick, onTagDelete }) => {
  const [more, setMore] = useState(false)
  const tagsRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const height = tagsRef.current?.offsetHeight
    /**
     * На примере 3 строк:
     * Высота 3 строк + отступ между строками
     * 3 строки 2 междустрочие по 8 пикселей
     */
    const heightLess = tagHeight * rowsCount + 8 * (rowsCount - 1)

    if (height && height > heightLess) {
      setMore(true)
    }
  }, [rowsCount])

  const handleMoreClick: ComponentProps<typeof More>['onClick'] = (event) => {
    onMoreClick?.(event)
    setMore(false)
  }

  return (
    <Parent ref={tagsRef}>
      <List
        style={
          more
            ? {
                overflow: 'hidden',
                // prettier-ignore
                height: `calc(${tagHeight * rowsCount}px + var(--spacing-8px, 8px) * ${rowsCount - 1})`,
              }
            : undefined
        }
      >
        {tags.map(({ text, matchMy, interestId }, index) => {
          const _index = index % Object.keys(matchColor).length

          return (
            <ParentTag
              key={interestId}
              data-name={`test-${interestId}-${index}`}
            >
              <Tag
                tagId={interestId}
                text={text}
                type={_index as TagTypes}
                match={matchMy}
                active={true}
                onClick={() => onClick(interestId)}
                onTagDelete={onTagDelete}
              />
            </ParentTag>
          )
        })}
      </List>
      {more && (
        <ParentMore>
          <More onClick={handleMoreClick} />
        </ParentMore>
      )}
    </Parent>
  )
}

const Parent = styled.div`
  display: grid;
  gap: var(--spacing-24px);
  grid-gap: var(--spacing-24px);
`

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;

  @supports (display: flex) and (gap: 1em) {
    gap: var(--spacing-8px);
  }
`

const ParentTag = styled.li`
  margin-bottom: var(--spacing-8px);

  html[dir='ltr'] & {
    margin-right: var(--spacing-8px);
  }

  html[dir='rtl'] & {
    margin-left: var(--spacing-8px);
  }

  @supports (display: flex) and (gap: 1em) {
    html[dir='ltr'] &,
    html[dir='rtl'] & {
      margin: 0;
    }
  }
`

const ParentMore = styled.div`
  display: flex;
  justify-content: center;
`
